import { useEffect } from "react";
import { useSetAtom } from "jotai";
import { meApi } from "../api/client";
import { setLocaleAction, type Locale } from "../stores/locale";
import { setTimezoneAction } from "../stores/timezone";

// useHydratePreferences pulls the caller's server-stored display preferences
// (Phase 2) once on an authenticated mount and overwrites the client-persisted
// atoms with them — the SERVER is the source of truth, so the language/timezone a
// user picked on one device follows them to the next.
//
// Why overwrite through the setter actions (not setAtom directly): the setters
// also write localStorage, so the hydrated server value becomes the device's
// offline fallback for the next cold start (before this fetch resolves).
//
// Fallbacks are deliberate and total:
//   - A NULL field (unset on the server) leaves the current client default in
//     place (localStorage / Intl detection) — Phase-1 behavior is never clobbered.
//   - An API failure is swallowed: a transient /me/preferences error must never
//     blank the UI's language or shift its clock. We just keep the client default.
//
// It runs only when authenticated (the caller passes that in) so a logged-out
// landing page makes no /me call.
export function useHydratePreferences(isAuthenticated: boolean): void {
  const setLocale = useSetAtom(setLocaleAction);
  const setTimezone = useSetAtom(setTimezoneAction);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    meApi
      .getPreferences()
      .then((prefs) => {
        if (cancelled) return;
        // null = unset → keep the client default (don't overwrite with null).
        if (prefs.locale) setLocale(prefs.locale as Locale);
        if (prefs.timezone) setTimezone(prefs.timezone);
      })
      .catch(() => {
        // Graceful: keep the client default on any failure — never break the page.
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, setLocale, setTimezone]);
}
